export const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';

const GENERIC_MESSAGE = 'Something went wrong while signing in. Please try again.';
const CANCELLED = 'Sign-in was cancelled. Please try again.';
const BAD_CREDENTIALS = 'Incorrect email or password.';
const METHOD_UNAVAILABLE = 'This sign-in method is not available yet.';

const MESSAGES: Record<string, string> = {
    'auth/popup-closed-by-user': CANCELLED,
    'auth/cancelled-popup-request': CANCELLED,
    'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups and try again.',
    'auth/unauthorized-domain': 'This domain is not authorized for sign-in.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    [ACCOUNT_EXISTS_CODE]: 'An account already exists with this email. Sign in with the method you used before.',
    'auth/invalid-credential': BAD_CREDENTIALS,
    'auth/wrong-password': BAD_CREDENTIALS,
    'auth/user-not-found': BAD_CREDENTIALS,
    'auth/email-already-in-use': 'An account with this email already exists. Sign in instead.',
    'auth/weak-password': 'That password is too weak. Use at least 8 characters.',
    'auth/invalid-email': 'Enter a valid email address.',
    'auth/missing-email': 'Enter your email address.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    'auth/expired-action-code': 'This link has expired. Request a new one.',
    'auth/invalid-action-code': 'This link is invalid or has already been used. Request a new one.',
    'auth/credential-already-in-use': 'That sign-in method already belongs to another account.',
    'auth/provider-already-linked': 'That sign-in method is already connected to your account.',
    'auth/operation-not-allowed': METHOD_UNAVAILABLE,
    'auth/admin-restricted-operation': METHOD_UNAVAILABLE,
    'auth/user-disabled': 'This account has been disabled.',
};

function stringField(value: unknown, key: string): string | undefined {
    if (typeof value !== 'object' || value === null) return undefined;
    const field = (value as Record<string, unknown>)[key];
    return typeof field === 'string' ? field : undefined;
}

export function authErrorCode(error: unknown): string | undefined {
    return stringField(error, 'code');
}

/** The email Firebase attaches to `account-exists-with-different-credential`. */
export function authErrorEmail(error: unknown): string | undefined {
    const customData = typeof error === 'object' && error !== null
        ? (error as { customData?: unknown }).customData
        : undefined;
    return stringField(customData, 'email');
}

/**
 * Player-facing text for anything the auth facades throw. Firebase errors are
 * mapped by code so raw SDK text ("Firebase: Error (auth/...)") never reaches
 * the UI; a plain Error without a code is one of our own messages and passes
 * through. Pass `providerName` for a social popup, where `invalid-credential`
 * means the provider's OAuth exchange failed rather than a wrong password.
 */
export function authErrorMessage(error: unknown, providerName?: string): string {
    const code = authErrorCode(error);
    if (providerName && code === 'auth/invalid-credential') {
        return `${providerName} sign-in failed. Please try again or use another sign-in method.`;
    }
    if (code) return MESSAGES[code] ?? GENERIC_MESSAGE;
    if (error instanceof Error && error.message) return error.message;
    return GENERIC_MESSAGE;
}
